import classNames from "classnames";
import { has, isBoolean, isNil, merge, some } from "lodash";
import AppContext from "PFApp/app_context";
import WarningIcons from "PFApp/booking/components/warning_icons";
import { Alert } from "PFComponents/alert";
import ConfirmModal, { ConfirmModalHeader } from "PFComponents/confirm/confirm_modal";
import { LoadingDots } from "PFComponents/loading_dots";
import InputFieldSet from "PFComponents/text/input_field_set";
import TextArea from "PFComponents/text/text_area";
import { Typography } from "PFComponents/typography";
import { getProfileName } from "PFCore/helpers/profile";
import { useTemplate } from "PFCore/helpers/templates";
import useIsPermittedTo from "PFCore/helpers/use_is_permitted_to";
import { useDateFormatter } from "PFCore/hooks/use_date_formatter";
import { PermissionRule } from "PFTypes";
import PropTypes from "prop-types";
import React, { useContext, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { EngagementSelect } from "../../../components/engagement_select";
import BookingsTotal from "../../parts/overview/bookings_total";
import { activityTypeTranslationKeys } from "../../parts/overview/detail_view/booking_detail/booking_details_activity";
import BookingActivityContextProvider, {
  useBookingActivityContext
} from "../../parts/providers/booking_activity_context_provider";
import { BookingSelectOption } from "../booking_questions/booking_questions";
import UpdateQuestionsModal from "../booking_questions/update_questions_modal";
import { useDetailsPanelContext } from "../details_panel";
import { ReassignSelectOption } from "../reassign_booking_modal/bookings_to_reassign_selector";
import { ReassignBookingModal, ReassignMode } from "../reassign_booking_modal/reassign_booking_modal";
import { ActivityInfo } from "./activity_info";
import { AvailabilityInfo } from "./availability_info";
import { BookingCategorySelect } from "./booking_category_select";
import css from "./booking_modal.module.scss";
import { getAcceptButtonTranslationKey, getHeaderTitleTranslationKey } from "./booking_modal.utils";
import { useBookingModalContext } from "./booking_modal_context_provider";
import { BookingRangeColumn } from "./booking_range_column";
import { OverrideToggles } from "./override_toggles";
import { isSomeRequirementInvalid } from "./requirement_selector";
import useBookingInitialData from "./use_booking_initial_data";
import { BookingModalMode } from "./use_booking_modal";
import { useCategoryError } from "./use_category_error";
import { useEngagementSelect } from "./use_engagement_select";
import useHandleSubmit from "./use_handle_submit";
import usePotentialWarnings from "./use_potential_warnings";
import { WorkforceMemberSelect } from "./workforce_member";

const PROFILES_COUNT_LIMIT = 50;

const isRepeatedBooking = (bookings) => some(bookings, (booking) => has(booking, "wdayMask"));

const BookingModalContent = ({ onClose, initialData = {}, bookingId, onSuccess, onSubmit, mode }) => {
  const {
    store: { currentProfile }
  } = useContext(AppContext);

  const {
    modal: { close }
  } = useBookingModalContext();
  const { closeDetailsPanel } = useDetailsPanelContext();
  const { activity } = useBookingActivityContext();

  const { t } = useTranslation(["bookingModule", "translation"]);

  const [index, setIndex] = useState(0);
  const [showQuestionsModal, setShowQuestionsModal] = useState(false);
  const [showReassignModal, setShowReassignModal] = useState(false);
  const [bookingData, setBookingData] = useState({});
  const specificTime = !!Object.values(bookingData || {}).find((item) => item.wdayMask);
  const [updateGroupOptionSelected, setUpdateGroupOptionSelected] = useState(BookingSelectOption.This);
  const [updateRepeatOptionSelected, setUpdateRepeatOptionSelected] = useState(BookingSelectOption.All);
  const [reassignOptionSelected, setReassignOptionSelected] = useState(ReassignSelectOption.ThisBooking);
  const [overridesCalendar, setOverridesCalendar] = useState(
    isBoolean(initialData.overridesCalendar) ? initialData.overridesCalendar : true
  );
  const [overridesNonWorkingDays, setOverridesNonWorkingDays] = useState(
    isBoolean(initialData.overridesNonWorkingDays) ? initialData.overridesNonWorkingDays : false
  );
  const [exceedsCalendarHours, setExceedsCalendarHours] = useState(undefined); // initial state is undefined to indicate it wasn't yet fetched
  const [includesNonWorkingDays, setIncludesNonWorkingDays] = useState(undefined); // initial state is undefined to indicate it wasn't yet fetched

  const { utc } = useDateFormatter();
  const activityTemplate = useTemplate(activity?.template_id);
  const isPermittedTo = useIsPermittedTo(currentProfile);

  const isGrouped = Number.isInteger(initialData.bookingGroupId);
  const isBookingResourcer = isPermittedTo(PermissionRule.BookingResourcer);
  const isAuditBooking = activityTemplate?.key.includes("audit");

  const {
    title,
    setTitle,
    description,
    setDescription,
    selectedUser,
    setSelectedUser,
    prefilledUserRef,
    selectedEngagement,
    setSelectedEngagement,
    selectedBookingCategory,
    setSelectedBookingCategory,
    bookingItems,
    setBookingItems,
    isBookingsFromAvailabilityLoading
  } = useBookingInitialData(initialData, mode === BookingModalMode.Create);

  const categoryError = useCategoryError({
    mode,
    selectedBookingCategory,
    isDemandRelated: !!selectedEngagement || !!initialData.parentActivityId || !!initialData.activityId
  });

  const {
    shouldDisplayEngagementSelect,
    engagementSelectQuery,
    engagementSelectParseResponse,
    engagementSelectHandleChange,
    parseEngagementToOption
  } = useEngagementSelect({
    activityId: initialData.activityId,
    selectedEngagement,
    setSelectedEngagement,
    title,
    setTitle,
    selectedUser,
    isGrouped
  });

  useEffect(() => {
    if (index > bookingItems.length) {
      setIndex(0);
    }
  }, [bookingItems.length]);

  const resetValues = () => {
    setSelectedUser(null);
    setBookingItems([]);
    setBookingData(null);
    setSelectedBookingCategory(null);
  };

  const shouldDisplayOverridesCalendarToggle = exceedsCalendarHours || isGrouped || !!selectedUser?.search_id;
  const showOverridesNonWorkingDaysToggle = includesNonWorkingDays && !isRepeatedBooking(bookingData);

  useEffect(() => {
    if (!shouldDisplayOverridesCalendarToggle && !isNil(exceedsCalendarHours)) {
      setOverridesCalendar(true);
    }
  }, [shouldDisplayOverridesCalendarToggle]);

  const {
    handleSubmit,
    isLoading,
    errors: submitErrors
  } = useHandleSubmit({
    bookingData,
    initialData,
    title,
    description,
    prefilledUserRef,
    selectedUser,
    selectedBookingCategory,
    mode,
    bookingId,
    specificTime,
    overridesCalendar: shouldDisplayOverridesCalendarToggle ? overridesCalendar : undefined,
    overridesNonWorkingDays: showOverridesNonWorkingDaysToggle ? overridesNonWorkingDays : undefined,
    onSubmit,
    onSuccess: () => {
      onSuccess?.();
      handleClose();
      if (specificTime && [BookingModalMode.Edit, BookingModalMode.Clone].includes(mode)) {
        closeDetailsPanel();
      }
    },
    onError: () => handleClose(),
    updateGroupOptionSelected,
    updateRepeatOptionSelected,
    reassignOptionSelected,
    roleBookingsIds: initialData.roleBookingsIds,
    selectedEngagement
  });

  const {
    overbookings,
    misalignments,
    errors: potentialWarningsErrors,
    loading: isPotentialWarningsLoading
  } = usePotentialWarnings({
    bookingId,
    bookingData,
    activityId: initialData.activityId ?? selectedEngagement?.id,
    selectedUser,
    selectedBookingCategory,
    disabled: isLoading,
    onCancel: () => {
      setExceedsCalendarHours(false);
      setIncludesNonWorkingDays(false);
    },
    onSuccess: ({ exeeds_calendar_hours, includes_non_working_days }) => {
      setExceedsCalendarHours(exeeds_calendar_hours);
      setIncludesNonWorkingDays(includes_non_working_days);
    }
  });

  const inputTagRef = useRef();

  const handleClose = () => {
    onClose?.();
    resetValues();
    close();
  };

  const isSubmitEnabled =
    !isLoading &&
    !isPotentialWarningsLoading &&
    selectedUser &&
    selectedBookingCategory &&
    bookingItems.length > 0 &&
    (selectedUser?.total || 0) <= PROFILES_COUNT_LIMIT &&
    !isSomeRequirementInvalid(Object.values(bookingData || {})) &&
    !categoryError;

  const handleOK = () => {
    const isSingleDayBookingTemplate =
      initialData.bookingTemplate &&
      utc(initialData.bookingTemplate.end_date).diff(initialData.bookingTemplate.start_date, "days") === 0;
    const shouldShowReassignModal =
      mode === BookingModalMode.Edit &&
      selectedUser &&
      initialData.profileId &&
      initialData.profileId !== selectedUser?.id;

    if (shouldShowReassignModal) {
      setShowReassignModal(true);
    } else if (
      mode === BookingModalMode.Edit &&
      ((isGrouped && !initialData.bookingTemplate) || (specificTime && !isSingleDayBookingTemplate))
    ) {
      setShowQuestionsModal(true);
    } else {
      handleSubmit();
    }
  };

  const handleFocus = () => {
    inputTagRef?.current?.select();
  };

  const confirmModalHeader = getHeaderTitleTranslationKey(mode);

  return (
    <>
      <ConfirmModal
        handleOK={isSubmitEnabled ? handleOK : null}
        autoHandleClose={false}
        handleClose={handleClose}
        handleCancel={handleClose}
        classes={{
          dialog: css.modalDialog,
          modal: css.modal,
          confirm: css.modalConfirm,
          sectionBox: css.sectionBox,
          button: css.confirmButton
        }}
        footerLabel={
          <div className={css.footer}>
            <BookingsTotal
              bookings={(bookingData && Object.values(bookingData)) || []}
              isEditMode={mode !== BookingModalMode.Create}
            />
            <WarningIcons overbookings={overbookings} misalignments={misalignments} />
          </div>
        }
        labelOK={
          <div className={css.labelOK}>
            {isPotentialWarningsLoading ? <LoadingDots /> : t(getAcceptButtonTranslationKey(mode))}
          </div>
        }
      >
        <ConfirmModalHeader>
          <div className={css.header}>{t(confirmModalHeader)}</div>
        </ConfirmModalHeader>
        <div className={css.wrapper}>
          <div className={css.column}>
            {(shouldDisplayOverridesCalendarToggle || showOverridesNonWorkingDaysToggle) && (
              <OverrideToggles
                value={{ overridesCalendar, overridesNonWorkingDays }}
                showOverridesCalendar={shouldDisplayOverridesCalendarToggle}
                showOverridesNonWorkingDays={showOverridesNonWorkingDaysToggle}
                onOverridesCalendarChange={setOverridesCalendar}
                onOverridesNonWorkingDaysChange={setOverridesNonWorkingDays}
              />
            )}
            {initialData.availability && <AvailabilityInfo availability={initialData.availability} />}
            <WorkforceMemberSelect
              selectedUser={selectedUser}
              setSelectedUser={setSelectedUser}
              mode={mode}
              initialData={initialData}
              isDisabled={!isBookingResourcer}
            />
            {shouldDisplayEngagementSelect && (
              <EngagementSelect
                selectedEngagement={selectedEngagement}
                letClear
                query={engagementSelectQuery}
                parseResponse={engagementSelectParseResponse}
                handleChange={engagementSelectHandleChange}
                parseEngagementToOption={parseEngagementToOption}
              />
            )}
            {selectedUser?.profiles?.length > 1 && (
              <Alert
                kind={initialData.total > PROFILES_COUNT_LIMIT ? "error" : "warning"}
                small
                title={t("bookingModule:bookings.create.bulk.warning", {
                  profilesCount: selectedUser?.total
                })}
                multiLineTitle
              >
                {initialData.total > PROFILES_COUNT_LIMIT &&
                  t("bookingModule:bookings.create.bulk.tooManyProfiles", {
                    profilesCount: PROFILES_COUNT_LIMIT
                  })}
              </Alert>
            )}
            {initialData.activityId && !shouldDisplayEngagementSelect && (
              <ActivityInfo
                label={t(`translation:${activityTypeTranslationKeys[activityTemplate?.key]}`)}
                activityId={initialData.activityId}
              />
            )}
            {initialData.parentActivityId && !shouldDisplayEngagementSelect && (
              <ActivityInfo
                label={isAuditBooking ? t("translation:auditEngagement") : t("translation:engagement")}
                activityId={initialData.parentActivityId}
              />
            )}
          </div>
          <BookingRangeColumn
            index={index}
            initialData={initialData}
            setIndex={setIndex}
            setBookingItems={setBookingItems}
            setBookingData={setBookingData}
            bookingItems={bookingItems}
            isEditMode={mode !== BookingModalMode.Create}
            loading={isBookingsFromAvailabilityLoading}
            errors={submitErrors}
            isSelectedEngagement={!!selectedEngagement}
          />

          <div className={css.column}>
            <BookingCategorySelect
              isRoleRelated={!!initialData.activityId || !!selectedEngagement}
              category={selectedBookingCategory}
              onCategoryChange={(newCategory) => {
                const isTitlePreviousCategoryName = selectedBookingCategory?.display_as === title;
                if (newCategory && (!title || isTitlePreviousCategoryName)) {
                  setTitle(newCategory.display_as);
                }
                setSelectedBookingCategory(newCategory);
              }}
              className={css.selectInput}
              error={
                submitErrors?.booking_category_id ||
                potentialWarningsErrors?.booking_category_id ||
                categoryError
              }
            />
            <InputFieldSet
              label={t("bookingModule:bookings.create.title")}
              controlledValue
              value={title}
              maxLength={250}
              handleFocus={handleFocus}
              handleChange={setTitle}
              rootClassName={classNames(css.selectInput)}
              inputTagRef={inputTagRef}
            />
            <div>
              <Typography variant="bodyRegular" className={css.label}>
                {t("bookingModule:bookings.create.description")}
              </Typography>
              <TextArea
                displayAbove
                controlledValue
                value={description}
                onChange={setDescription}
                className={css.textarea}
                minHeight={145}
                placeholder={t("bookingModule:bookings.create.descriptionPlaceholder")}
              />
            </div>
          </div>
        </div>
      </ConfirmModal>
      {showQuestionsModal && (
        <UpdateQuestionsModal
          profileFullName={getProfileName(selectedUser)}
          onClose={() => setShowQuestionsModal(false)}
          onSubmit={handleSubmit}
          updateGroupOptionSelected={updateGroupOptionSelected}
          setUpdateGroupOptionSelected={setUpdateGroupOptionSelected}
          updateRepeatOptionSelected={updateRepeatOptionSelected}
          setUpdateRepeatOptionSelected={setUpdateRepeatOptionSelected}
          isRepeated={specificTime}
          bookingGroupId={initialData.bookingGroupId}
        />
      )}
      {showReassignModal && (
        <ReassignBookingModal
          mode={ReassignMode.Custom}
          roleName={activity.name}
          profileFrom={prefilledUserRef.current ?? selectedUser}
          // TODO: [PROF-3444] Handle matching scores section when the API/HAL part is ready
          profileTo={{
            profile: selectedUser
          }}
          readOnlyMode
          roleBookingsIds={initialData.roleBookingsIds ?? 0}
          reassignOptionSelected={reassignOptionSelected}
          setReassignOptionSelected={setReassignOptionSelected}
          onClose={() => setShowReassignModal(false)}
          onSubmit={handleSubmit}
        />
      )}
    </>
  );
};

const BookingModal = ({ initialData = {}, ...modalProps }) => {
  const {
    modal: {
      state: { isOpen, data, props }
    }
  } = useBookingModalContext();

  if (!isOpen) {
    return null;
  }

  return (
    <BookingActivityContextProvider id={data?.activityId}>
      <BookingModalContent initialData={merge({}, data, initialData)} {...props} {...modalProps} />
    </BookingActivityContextProvider>
  );
};

const props = {
  onClose: PropTypes.func,
  onSubmit: PropTypes.func,
  bookingId: PropTypes.number,
  mode: PropTypes.oneOf(["create", "edit", "clone"]),
  initialData: PropTypes.shape({
    activityId: PropTypes.number,
    profileId: PropTypes.number,
    title: PropTypes.string,
    description: PropTypes.string,
    startDate: PropTypes.string,
    endDate: PropTypes.string,
    categoryId: PropTypes.number,
    bookings: PropTypes.arrayOf(
      PropTypes.shape({
        startDate: PropTypes.string,
        endDate: PropTypes.string,
        load: PropTypes.number
      })
    )
  }),
  onSuccess: PropTypes.func
};

BookingModal.propTypes = props;
BookingModalContent.propTypes = props;

export default BookingModal;
